import { Injectable } from '@angular/core';

import { Observable, Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { LoggerService } from './logger.service';

interface MessageDetails<T> {
  type: string;
  origin: string;
  payload?: T;
}

interface WindowMessage {
  origin: string;
  data: {
    type: string;
    payload: unknown;
  };
}

export enum WindowMessagesAllowedOrigins {
  BilbaytCDN = 'https://cdn.bilbayt.com',
  PaymentPage = 'https://app-payment-page.bilbayt.com',
}

@Injectable({ providedIn: 'root' })
export class WindowMessagesService {
  private messagesDispatcher$ = new Subject<MessageDetails<unknown>>();

  constructor(private loggerService: LoggerService) {
    onmessage = (message: WindowMessage) => {
      if (!message?.data || !message?.origin) {
        return;
      }

      const { origin } = message;

      // Only allow messages from known sources
      const allowed = Object.values(WindowMessagesAllowedOrigins).some((allowedOrigin) => origin.includes(allowedOrigin));

      if (allowed) {
        const { type, payload } = message.data;

        console.log(`[WindowMessagesService] received message of type ${type} from origin ${origin}`);
        this.messagesDispatcher$.next({ type, payload, origin });
      }
    };
  }

  public sendMessage({
    type,
    payload,
    targetWindow,
    targetOrigin,
  }: {
    type: string;
    payload?: unknown;
    targetWindow: Window;
    targetOrigin: WindowMessagesAllowedOrigins;
  }): void {
    if (!targetWindow || !targetOrigin) {
      return;
    }

    try {
      console.log(`[WindowMessagesService] about to send message of type ${type}`);
      targetWindow.postMessage({ type, payload: payload ?? null }, targetOrigin);
    } catch (error) {
      this.loggerService.info({ component: 'WindowMessagesService', message: null, details: { error } });
    }
  }

  public onMessage<T>(type: string, targetOrigin: string): Observable<T> {
    return this.messagesDispatcher$.asObservable().pipe(
      filter((message) => message.type === type && message.origin.includes(targetOrigin)),
      map(({ payload }) => payload as T),
    );
  }

  public onMessages({ fromOrigin }: { fromOrigin: string }): Observable<{ type: string; payload: unknown }> {
    return this.messagesDispatcher$.asObservable().pipe(
      filter((message) => message.origin.includes(fromOrigin)),
      map(({ type, payload }) => ({ type, payload })),
    );
  }
}
